import React, { useEffect } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/themes/material_green.css';
import { ReactModel } from '../../react-model/react-model';
import { Instance } from 'flatpickr/dist/types/instance';
import { DateLimit, DateOption } from 'flatpickr/dist/types/options';
import { useValidator } from '../hooks/use-validator';
import { useStaticMemo } from '../hooks/use-static-memo';
// import { useOnModelChange } from '../hooks/use-on-model-change';

export interface HDatetimePickerProps<T> extends React.InputHTMLAttributes<any> {
	model: ReactModel<T>;
	field: keyof T;
	/**https://flatpickr.js.org/formatting/
	 * Y-M-d
	 *
Character	Description									[Example]
-----------------------------------------------------------------------
-------------- year --------------------
y	A two digit representation of a year				[99 or 03]
Y	A full numeric representation of a year, 4 digits	[1999 or 2003]

-------------- month --------------------
F	A full textual representation of a month			[January through December]
m	Month 01-12											[01 through 12]
n	Month 1-12											[1 through 12]
M	A short textual representation of a month			[Jan through Dec]

-------------- day of the month ------------
d	Day of the month									[01 to 31]
j	Day of the month 									[1 to 31]
J	Day of the month									[1st, 2nd, to 31st]

-------------- day of the week ----------
D	A textual representation of a day					[Mon through Sun]
l (lowercase 'L') day of the week 						[Sunday through Saturday]
w	Numeric representation of the day of the week		[0 (for Sunday) through 6 (for Saturday)]

-------------- week of the year ---------
W	week 0-52											[0 (first week) through 52 (last week)]

-------------- seconds --------------------
U	The number of seconds since the Unix Epoch			[1413704993]

-------------- date string ----------------
Z	ISO Date format										[2017-03-04T01:23:43.000Z]




-------------- time format ----------------
H	Hours (24 hours)					[00 to 23]
h	Hours								[1 to 12]
G	Hours, 2 digits with leading zeros	[1 to 12]
i	Minutes								[00 to 59]
S	Seconds, 2 digits					[00 to 59]
s	Seconds								[0, 1 to 59]
K	AM/PM								[AM or PM]

-------------- escape format ----------------
dateFormat: "Y-m-d\\Z", // Displays: 2017-01-22Z

-------------- template ---------------------
Y-m-d H:i:S
*/
	display_format?: string;

	place_holder?: string;

	/** If true, then time only */
	no_calendar?: boolean;
	enable_time?: boolean;
	enable_seconds?: boolean;
	time_24hr?: boolean;
	hour_increment?: number;
	minute_increment?: number;

	min_date?: 'today' | Date;
	max_date?: 'today' | Date;

	/** disabled_dates and  enabled_dates are mutually exclusive */
	disabled_dates?: DateLimit<DateOption>[];
	/** disabled_dates and  enabled_dates are mutually exclusive */
	enabled_dates?: DateLimit<DateOption>[];

	no_popover?: boolean;

	onInstCreated?:(instance: Instance)=>void;

	// the following needs extra effort
	// /** Set the component properties when then model changes */
	// onModelChange?: (state: T) => void | React.HTMLAttributes<HTMLInputElement>;
	// /** Set the model when then input element changes */
	// onViewChange?: (state: T, value: any) => void | T;


	/**output value. We may need to call picker.parseDate(...) */
	// api?: { picker?: Instance; comp?: HDatetimePicker<T> };
}

export function HDatetimePicker<T>({
	model,
	field,
	// style,

	display_format,
	place_holder,
	enable_time,
	enable_seconds,
	time_24hr,
	hour_increment,
	minute_increment,

	no_calendar,
	min_date,
	max_date,

	disabled_dates,
	enabled_dates,

	no_popover,
	onInstCreated,
	// onModelChange,
	// onViewChange,

	...props
}: HDatetimePickerProps<T>) {
	const { state, setState } = model.useModel();
	const ref = useStaticMemo(() => ({ current: null as any, instance: null as Instance | null }));

	if (ref.instance) {
		ref.instance.setDate(state[field] as any as Date);
	}

	if (!display_format) {
		display_format = 'Y-m-d';
	}
	if (!place_holder) {
		place_holder = 'Select Date...';
	}

	let picker_instance: Instance | null = null;
	useEffect(() => {
		picker_instance = flatpickr(ref.current, {
			wrap: false,
			dateFormat: display_format,
			allowInput: true,
			// appendTo: (this.props.appendTo || undefined) as any,
			//time
			time_24hr: time_24hr || true,
			enableTime: enable_time,
			hourIncrement: hour_increment || 1,
			minuteIncrement: minute_increment || 1,
			enableSeconds: enable_seconds,
			noCalendar: no_calendar,
			minDate: min_date,
			maxDate: max_date,

			// disable: disabled_dates,
			...(disabled_dates ? { disable: disabled_dates } : {}),
			...(enabled_dates ? { enable: enabled_dates } : {}),
		}); ///*, inline:true this cause error in iframe*/});
		ref.instance = picker_instance;
		//initialize the current date
		// const value = this.props.scope.values[this.props.model];
		const value = state[field];
		picker_instance.setDate(value as any as Date, false);
		//when the UI changes, set value
		picker_instance.config.onChange.push((dates) => {
			// const date_value = new Date(dates[0]);
			// onViewChange?.(date_value, model.getState());
			model.setProperty({ [field]: new Date(dates[0]) } as any);
		});
		onInstCreated?.(picker_instance);
		return () => {
			ref.instance?.destroy();
		};
	}, [model, //rerender if changes
		field,
		//
		display_format,
		place_holder,
		enable_time,
		enable_seconds,
		time_24hr,
		hour_increment,
		minute_increment,

		no_calendar,
		min_date,
		max_date,

		disabled_dates,
		enabled_dates,]);

	props = useValidator(model, field, ref, props, no_popover);

	// useOnModelChange(model, onModelChange, props);

	// const styles = Object.assign({ whiteSpace: 'nowrap' }, style || {});

	return (
		<>
			<input ref={ref} type="text" {...props} data-input />
			{/* <span>
				<a
					className="input-button"
					data-bs-toggle
					style={{
						cursor: 'pointer',
						position: 'absolute',
						left: '-2em',
						top: '0em',
					}}
				>
					<FaRegCalendarAlt />
				</a>

				<a
					className="input-button"
					title="Clear"
					data-clear
					style={{
						cursor: 'pointer',
						position: 'relative',
						left: '-2em',
						top: '0em',
					}}
				>
					<MdClose />
				</a>
			</span> */}
		</>
	);
}
